import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import Rating from "@/components/base/Rating";
import StatCard from "./components/StatCard";
import TrendChart from "./components/TrendChart";
import { useAuth } from "@/context/AuthContext";
import { getOwnerDashboard, getOwnerListings, getOwnerOrders, getOwnerReviews } from "@/services/ownerApi";
import type { OwnerDashboardData, OwnerReview } from "@/services/ownerApi";
import type { OwnedListing, OwnerOrder } from "@/types";

export default function Dashboard() {
  const { user, token } = useAuth();
  const [data, setData] = useState<OwnerDashboardData | null>(null);
  const [listings, setListings] = useState<OwnedListing[]>([]);
  const [orders, setOrders] = useState<OwnerOrder[]>([]);
  const [reviews, setReviews] = useState<OwnerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([
      getOwnerDashboard(token),
      getOwnerListings(token),
      getOwnerOrders(token),
      getOwnerReviews(token),
    ])
      .then(([d, l, o, r]) => {
        if (!active) return;
        setData(d);
        setListings(l);
        setOrders(o);
        setReviews(r);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center">
        <i className="ri-error-warning-line text-2xl text-secondary-600"></i>
        <p className="mt-2 text-sm text-secondary-900">We couldn't load your dashboard right now.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-refresh-line"></i> Retry
        </button>
      </div>
    );
  }

  const { stats, trend } = data;
  const pending = listings.filter((l) => l.status === "pending");
  const topListings = [...listings].sort((a, b) => b.views - a.views).slice(0, 4);
  const recentOrders = orders.slice(0, 4);
  const recentReviews = reviews.slice(0, 3);
  const unanswered = reviews.filter((r) => !r.replied).length;

  return (
    <>
      <Seo title="Owner Dashboard" path="/dashboard" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
            Hello, {user?.name.split(" ")[0]}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">
            Here's how your listings are performing across the Mysore region.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/dashboard/listings/new"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-circle-line"></i> Add listing
          </Link>
          <Link
            to="/dashboard/listings"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-store-2-line"></i> Manage
          </Link>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="mt-6 flex flex-col gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-time-line text-lg"></i>
            </span>
            <div>
              <p className="text-sm font-bold text-foreground-950">
                {pending.length} listing{pending.length > 1 ? "s" : ""} awaiting review
              </p>
              <p className="mt-0.5 text-xs text-foreground-600">
                Our team usually approves new listings within one working day.
              </p>
            </div>
          </div>
          <Link
            to="/dashboard/listings"
            className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-900 whitespace-nowrap cursor-pointer"
          >
            View listings <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total listings" value={String(stats.totalListings)} icon="ri-store-2-line" hint={`${stats.approvedListings} approved`} tone="primary" />
        <StatCard label="Profile views" value={stats.profileViews.toLocaleString()} icon="ri-eye-line" change={stats.viewsChange} hint="vs last month" tone="neutral" />
        <StatCard label="Total leads" value={stats.totalLeads.toLocaleString()} icon="ri-user-received-line" change={stats.leadsChange} hint="vs last month" tone="accent" />
        <StatCard label="Avg. rating" value={stats.avgRating.toFixed(1)} icon="ri-star-line" hint={`${stats.totalReviews.toLocaleString()} reviews`} tone="secondary" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">Views &amp; leads</h2>
              <p className="mt-0.5 text-xs text-foreground-500">Last 8 months of activity</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5 text-foreground-600">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary-500"></span> Views
              </span>
              <span className="inline-flex items-center gap-1.5 text-foreground-600">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent-500"></span> Leads
              </span>
            </div>
          </div>
          <div className="mt-4">
            <TrendChart data={trend} />
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Quick actions</h2>
          <div className="mt-4 space-y-2.5">
            {[
              { to: "/dashboard/listings/new", icon: "ri-add-box-line", label: "Add a new listing" },
              { to: "/dashboard/reviews", icon: "ri-chat-check-line", label: `Reply to reviews${unanswered ? ` (${unanswered})` : ""}` },
              { to: "/dashboard/media", icon: "ri-upload-2-line", label: "Upload photos" },
              { to: "/listing-plans", icon: "ri-vip-crown-line", label: "Upgrade your plan" },
              { to: "/dashboard/profile", icon: "ri-user-settings-line", label: "Edit profile" },
            ].map((a) => (
              <Link
                key={a.to + a.label}
                to={a.to}
                className="flex items-center gap-3 rounded-md border border-background-200 px-3.5 py-3 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className={`${a.icon} text-lg text-primary-600`}></i>
                {a.label}
                <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">Top listings</h2>
            <Link to="/dashboard/listings" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              View all
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {topListings.map((l) => (
              <Link
                key={l.id}
                to={`/dashboard/listings/${l.id}`}
                className="flex items-center gap-3 rounded-md p-2 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <img src={l.image} alt={l.name} className="h-12 w-12 rounded-md object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground-950">{l.name}</p>
                  <p className="text-xs text-foreground-500">
                    {l.views.toLocaleString()} views · {l.leads} leads
                  </p>
                </div>
                <StatusBadge status={l.status} />
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">Recent reviews</h2>
            <Link to="/dashboard/reviews" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              View all
            </Link>
          </div>
          <div className="mt-4 space-y-4">
            {recentReviews.map((r) => (
              <div key={r.id} className="border-b border-background-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground-950">{r.authorName}</span>
                  <Rating value={r.rating} />
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-foreground-600">{r.comment}</p>
                <p className="mt-1 text-[11px] text-foreground-400">{r.businessName} · {r.date}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-base font-bold text-foreground-950">Recent orders</h2>
          <Link to="/dashboard/orders" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
            View all
          </Link>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                <th className="pb-2 font-semibold">Order</th>
                <th className="pb-2 font-semibold">Plan</th>
                <th className="pb-2 font-semibold">Amount</th>
                <th className="pb-2 font-semibold">Status</th>
                <th className="pb-2 font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o) => (
                <tr key={o.id} className="border-t border-background-100">
                  <td className="py-3 font-mono text-xs text-foreground-700">{o.orderNo}</td>
                  <td className="py-3 text-foreground-800">{o.planName}</td>
                  <td className="py-3 font-semibold text-foreground-900">₹{o.amount.toLocaleString("en-IN")}</td>
                  <td className="py-3"><StatusBadge status={o.status} /></td>
                  <td className="py-3 text-foreground-500">{o.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}